import React, { createContext, useContext, useState, useEffect } from 'react';

// Supported languages
export const LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸', dir: 'ltr' },
  es: { name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  fr: { name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  de: { name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  it: { name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  ja: { name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  zh: { name: '中文', flag: '🇨🇳', dir: 'ltr' },
  pt: { name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  ko: { name: '한국어', flag: '🇰🇷', dir: 'ltr' },
  hi: { name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  ar: { name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
};

// Translation strings
const translations = {
  en: {
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    search: 'Search',
    filter: 'Filter',
    
    // Auth
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    name: 'Name',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    
    // Landing
    heroTitle: 'CREATE GAMES WITH',
    heroTitleHighlight: 'ZERO CODE',
    heroSubtitle: 'Describe your game idea in plain English. Our AI builds playable games in seconds. Edit with drag-and-drop or chat with AI Copilot.',
    startCreating: 'Start Creating',
    exploreGames: 'Explore Games',
    aiPowered: 'AI-POWERED GAME CREATION',
    
    // Features
    featureAiGen: 'AI Game Generation',
    featureAiGenDesc: 'Describe your game in plain English. Our AI builds it in seconds.',
    featureEditor: 'Real-time 3D Editor',
    featureEditorDesc: 'Drag-and-drop interface with live preview. No coding required.',
    featureCopilot: 'AI Copilot',
    featureCopilotDesc: 'Chat with AI to modify your game. "Add enemies that chase the player"',
    featureAssets: 'Asset Generation',
    featureAssetsDesc: 'Generate sprites, textures, and 3D models with AI.',
    featureCode: 'Pro Mode',
    featureCodeDesc: 'Advanced controls and scripting for experienced developers.',
    featurePublish: 'Publish & Share',
    featurePublishDesc: 'One-click publishing. Share your games with the world.',
    
    // Dashboard
    myGames: 'My Games',
    newGame: 'New Game',
    projects: 'projects',
    noGames: 'No games yet',
    createFirst: 'Create your first game!',
    
    // Editor
    sceneHierarchy: 'Scene Hierarchy',
    properties: 'Properties',
    aiCopilotTitle: 'AI Copilot',
    askAi: 'Ask AI to modify your game...',
    publish: 'Publish',
    published: 'Published',
    play: 'Play',
    
    // Gallery
    communityGallery: 'Community Gallery',
    searchGames: 'Search games...',
    recent: 'Recent',
    popular: 'Popular',
    mostLiked: 'Most Liked',
    genres: 'Genres',
    noGamesFound: 'No games found',
    beFirst: 'Be the first to publish a game!',
    getStarted: 'Get Started',
    
    // Subscription
    free: 'Free',
    standard: 'Standard',
    premium: 'Premium',
    subscribe: 'Subscribe',
    currentPlan: 'Current Plan',
    upgrade: 'Upgrade',
    perMonth: '/month',
    
    // Support
    help: 'Help',
    faq: 'FAQ',
    contactSupport: 'Contact Support',
    tutorial: 'Tutorial',
    
    // Footer
    copyright: '© 2026 PlayMold. All rights reserved.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    disclaimer: 'Disclaimer',
    
    // Splash
    welcomeTo: 'Welcome to',
    selectLanguage: 'Select your language',
    continueBtn: 'Continue',
    poweredBy: 'Powered by AI',

    // Legal / policy pages
    privacyPolicyTitle: 'Privacy Policy',
    termsTitle: 'Terms of Service',
    lastUpdated: 'Last updated: Jan 2026',
    legalIntroPrivacy: 'This Privacy Policy describes how PlayMold collects, uses, and shares information when you use our services. We collect the minimum data needed to run the service and give you clear control.',
    legalIntroTerms: 'Welcome to PlayMold. By creating an account or using the service you agree to these Terms.',
    legalTranslationsNote: 'This page is authoritative in English. Localized versions coming soon.',
    trashTitle: 'My Trash',
    trashEmpty: 'Nothing in your trash.',
    trashProOnly: 'Upgrade to Pro to restore your own deleted projects.',
    restore: 'Restore',
  },
  es: {
    loading: 'Cargando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    create: 'Crear',
    back: 'Atrás',
    next: 'Siguiente',
    submit: 'Enviar',
    search: 'Buscar',
    filter: 'Filtrar',
    signIn: 'Iniciar Sesión',
    signUp: 'Registrarse',
    signOut: 'Cerrar Sesión',
    email: 'Correo',
    password: 'Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    name: 'Nombre',
    forgotPassword: '¿Olvidaste tu contraseña?',
    noAccount: '¿No tienes cuenta?',
    hasAccount: '¿Ya tienes cuenta?',
    heroTitle: 'CREA JUEGOS CON',
    heroTitleHighlight: 'CERO CÓDIGO',
    heroSubtitle: 'Describe tu idea de juego en español. Nuestra IA construye juegos jugables en segundos.',
    startCreating: 'Empezar a Crear',
    exploreGames: 'Explorar Juegos',
    aiPowered: 'CREACIÓN DE JUEGOS CON IA',
    myGames: 'Mis Juegos',
    newGame: 'Nuevo Juego',
    projects: 'proyectos',
    noGames: 'Sin juegos aún',
    createFirst: '¡Crea tu primer juego!',
    sceneHierarchy: 'Jerarquía de Escena',
    properties: 'Propiedades',
    aiCopilotTitle: 'Copiloto IA',
    askAi: 'Pide a la IA que modifique tu juego...',
    publish: 'Publicar',
    published: 'Publicado',
    play: 'Jugar',
    communityGallery: 'Galería Comunitaria',
    searchGames: 'Buscar juegos...',
    recent: 'Recientes',
    popular: 'Popular',
    mostLiked: 'Más Gustados',
    genres: 'Géneros',
    noGamesFound: 'No se encontraron juegos',
    beFirst: '¡Sé el primero en publicar un juego!',
    getStarted: 'Comenzar',
    free: 'Gratis',
    standard: 'Estándar',
    premium: 'Premium',
    subscribe: 'Suscribirse',
    currentPlan: 'Plan Actual',
    upgrade: 'Mejorar',
    perMonth: '/mes',
    help: 'Ayuda',
    faq: 'FAQ',
    contactSupport: 'Contactar Soporte',
    tutorial: 'Tutorial',
    copyright: '© 2026 PlayMold. Todos los derechos reservados.',
    terms: 'Términos de Servicio',
    privacy: 'Política de Privacidad',
    disclaimer: 'Aviso Legal',
    welcomeTo: 'Bienvenido a',
    selectLanguage: 'Selecciona tu idioma',
    continueBtn: 'Continuar',
    poweredBy: 'Impulsado por IA',
    privacyPolicyTitle: 'Política de Privacidad',
    termsTitle: 'Términos de Servicio',
    lastUpdated: 'Última actualización: Enero 2026',
    legalTranslationsNote: 'Esta página es autorizada en inglés. Traducciones completas próximamente.',
    trashTitle: 'Mi papelera',
    trashEmpty: 'La papelera está vacía.',
    trashProOnly: 'Actualiza a Pro para recuperar proyectos eliminados.',
    restore: 'Restaurar',
  },
  fr: {
    loading: 'Chargement...',
    save: 'Sauvegarder',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    back: 'Retour',
    signIn: 'Connexion',
    signUp: 'Inscription',
    signOut: 'Déconnexion',
    heroTitle: 'CRÉEZ DES JEUX AVEC',
    heroTitleHighlight: 'ZÉRO CODE',
    heroSubtitle: 'Décrivez votre idée de jeu en français. Notre IA crée des jeux jouables en secondes.',
    startCreating: 'Commencer à Créer',
    exploreGames: 'Explorer les Jeux',
    myGames: 'Mes Jeux',
    newGame: 'Nouveau Jeu',
    communityGallery: 'Galerie Communautaire',
    copyright: '© 2026 PlayMold. Tous droits réservés.',
    welcomeTo: 'Bienvenue sur',
    selectLanguage: 'Choisissez votre langue',
    continueBtn: 'Continuer',
    privacyPolicyTitle: 'Politique de Confidentialité',
    termsTitle: 'Conditions de Service',
    lastUpdated: 'Dernière mise à jour : Janvier 2026',
    legalTranslationsNote: 'Cette page fait foi en anglais. Traductions complètes bientôt.',
    trashTitle: 'Ma corbeille',
    trashEmpty: 'La corbeille est vide.',
    trashProOnly: 'Passez à Pro pour restaurer vos projets supprimés.',
    restore: 'Restaurer',
    upgrade: 'Passer à un plan supérieur',
  },
  de: {
    loading: 'Laden...',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    create: 'Erstellen',
    back: 'Zurück',
    signIn: 'Anmelden',
    signUp: 'Registrieren',
    signOut: 'Abmelden',
    heroTitle: 'ERSTELLE SPIELE MIT',
    heroTitleHighlight: 'NULL CODE',
    heroSubtitle: 'Beschreibe deine Spielidee auf Deutsch. Unsere KI erstellt spielbare Spiele in Sekunden.',
    startCreating: 'Erstellen Starten',
    exploreGames: 'Spiele Erkunden',
    myGames: 'Meine Spiele',
    newGame: 'Neues Spiel',
    communityGallery: 'Community-Galerie',
    copyright: '© 2026 PlayMold. Alle Rechte vorbehalten.',
    welcomeTo: 'Willkommen bei',
    selectLanguage: 'Wähle deine Sprache',
    continueBtn: 'Weiter',
    privacyPolicyTitle: 'Datenschutzrichtlinie',
    termsTitle: 'Nutzungsbedingungen',
    lastUpdated: 'Zuletzt aktualisiert: Januar 2026',
    legalTranslationsNote: 'Diese Seite ist auf Englisch maßgeblich. Vollständige Übersetzungen folgen.',
    trashTitle: 'Mein Papierkorb',
    trashEmpty: 'Papierkorb ist leer.',
    trashProOnly: 'Upgrade auf Pro, um Projekte wiederherzustellen.',
    restore: 'Wiederherstellen',
    upgrade: 'Upgrade',
  },
  ja: {
    loading: '読み込み中...',
    save: '保存',
    cancel: 'キャンセル',
    delete: '削除',
    edit: '編集',
    create: '作成',
    back: '戻る',
    signIn: 'ログイン',
    signUp: '新規登録',
    signOut: 'ログアウト',
    heroTitle: 'ゲームを作ろう',
    heroTitleHighlight: 'コード不要',
    heroSubtitle: '日本語でゲームのアイデアを説明してください。AIが数秒でプレイ可能なゲームを構築します。',
    startCreating: '作成を開始',
    exploreGames: 'ゲームを探す',
    myGames: 'マイゲーム',
    newGame: '新しいゲーム',
    communityGallery: 'コミュニティギャラリー',
    copyright: '© 2026 PlayMold. 全著作権所有。',
    welcomeTo: 'ようこそ',
    selectLanguage: '言語を選択',
    continueBtn: '続ける',
    privacyPolicyTitle: 'プライバシーポリシー',
    termsTitle: '利用規約',
    lastUpdated: '最終更新: 2026年1月',
    legalTranslationsNote: 'このページは英語版が正式です。完全翻訳は近日公開。',
    trashTitle: 'マイゴミ箱',
    trashEmpty: 'ゴミ箱は空です。',
    trashProOnly: '削除したプロジェクトを復元するにはProにアップグレード。',
    restore: '復元',
    upgrade: 'アップグレード',
  },
  zh: {
    loading: '加载中...',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    create: '创建',
    back: '返回',
    signIn: '登录',
    signUp: '注册',
    signOut: '退出',
    heroTitle: '创建游戏',
    heroTitleHighlight: '零代码',
    heroSubtitle: '用中文描述您的游戏想法。我们的AI会在几秒钟内构建可玩的游戏。',
    startCreating: '开始创建',
    exploreGames: '探索游戏',
    myGames: '我的游戏',
    newGame: '新游戏',
    communityGallery: '社区画廊',
    copyright: '© 2026 PlayMold. 保留所有权利。',
    welcomeTo: '欢迎来到',
    selectLanguage: '选择语言',
    continueBtn: '继续',
    privacyPolicyTitle: '隐私政策',
    termsTitle: '服务条款',
    lastUpdated: '最后更新：2026年1月',
    legalTranslationsNote: '此页面以英文版本为准。完整译本即将推出。',
    trashTitle: '我的回收站',
    trashEmpty: '回收站为空。',
    trashProOnly: '升级到Pro以恢复已删除的项目。',
    restore: '恢复',
    upgrade: '升级',
  },
  pt: {
    heroTitle: 'CRIE JOGOS COM',
    heroTitleHighlight: 'ZERO CÓDIGO',
    startCreating: 'Começar a Criar',
    exploreGames: 'Explorar Jogos',
    welcomeTo: 'Bem-vindo ao',
    selectLanguage: 'Selecione seu idioma',
    continueBtn: 'Continuar',
    copyright: '© 2026 PlayMold. Todos os direitos reservados.',
    privacyPolicyTitle: 'Política de Privacidade',
    termsTitle: 'Termos de Serviço',
    lastUpdated: 'Última atualização: Janeiro 2026',
    legalTranslationsNote: 'Esta página é autoritativa em inglês. Traduções completas em breve.',
    trashTitle: 'Minha lixeira',
    trashEmpty: 'A lixeira está vazia.',
    trashProOnly: 'Atualize para Pro para restaurar projetos apagados.',
    restore: 'Restaurar',
    upgrade: 'Atualizar',
  },
  ko: {
    heroTitle: '게임 만들기',
    heroTitleHighlight: '코드 없이',
    startCreating: '만들기 시작',
    exploreGames: '게임 탐색',
    welcomeTo: '환영합니다',
    selectLanguage: '언어 선택',
    continueBtn: '계속',
    copyright: '© 2026 PlayMold. 모든 권리 보유.',
    privacyPolicyTitle: '개인정보 처리방침',
    termsTitle: '서비스 이용약관',
    lastUpdated: '최종 업데이트: 2026년 1월',
    legalTranslationsNote: '이 페이지는 영어가 정식본입니다. 전체 번역 준비 중.',
    trashTitle: '내 휴지통',
    trashEmpty: '휴지통이 비어 있습니다.',
    trashProOnly: '삭제된 프로젝트를 복원하려면 Pro로 업그레이드하세요.',
    restore: '복원',
    upgrade: '업그레이드',
  },
  ar: {
    heroTitle: 'أنشئ ألعابًا',
    heroTitleHighlight: 'بدون كود',
    startCreating: 'ابدأ الإنشاء',
    exploreGames: 'استكشف الألعاب',
    welcomeTo: 'مرحبًا بك في',
    selectLanguage: 'اختر لغتك',
    continueBtn: 'متابعة',
    copyright: '© 2026 PlayMold. جميع الحقوق محفوظة.',
    privacyPolicyTitle: 'سياسة الخصوصية',
    termsTitle: 'شروط الخدمة',
    lastUpdated: 'آخر تحديث: يناير 2026',
    legalTranslationsNote: 'هذه الصفحة مرجعية بالإنجليزية. الترجمات الكاملة قريبًا.',
    trashTitle: 'سلة المهملات',
    trashEmpty: 'سلة المهملات فارغة.',
    trashProOnly: 'قم بالترقية إلى Pro لاستعادة المشاريع المحذوفة.',
    restore: 'استعادة',
    upgrade: 'ترقية',
  },
  it: {
    // Common
    loading: 'Caricamento...',
    save: 'Salva',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    create: 'Crea',
    back: 'Indietro',
    next: 'Avanti',

    // Navigation
    home: 'Home',
    dashboard: 'Dashboard',
    gallery: 'Galleria',
    pricing: 'Prezzi',
    settings: 'Impostazioni',
    logout: 'Esci',
    login: 'Accedi',
    signUp: 'Registrati',

    // Landing
    heroTitle: 'Crea giochi con IA',
    heroSubtitle: 'Nessun codice. Nessun limite. Solo pura creatività.',
    startCreating: 'Inizia a Creare',
    exploreGames: 'Esplora Giochi',
    features: 'Funzionalità',
    featureAI: 'Copilot IA',
    featureAIDesc: 'Descrivi il tuo gioco. La nostra IA lo costruisce.',
    featureVisual: 'Editor Visivo',
    featureVisualDesc: 'Modifica scene 3D senza codice.',
    featureCommunity: 'Comunità',
    featureCommunityDesc: 'Pubblica, remixa, condividi.',
    footerTagline: 'La piattaforma di creazione giochi con IA.',
    quickLinks: 'Link Rapidi',
    legal: 'Legale',
    copyright: '© 2026 PlayMold. Tutti i diritti riservati.',
    privacy: 'Privacy',
    terms: 'Termini',

    // Splash
    welcomeTo: 'Benvenuto in',
    selectLanguage: 'Seleziona la tua lingua',
    continueBtn: 'Continua',
    poweredBy: 'Alimentato da IA',

    // Legal + trash
    privacyPolicyTitle: 'Informativa sulla Privacy',
    termsTitle: 'Termini di Servizio',
    lastUpdated: 'Ultimo aggiornamento: Gennaio 2026',
    legalTranslationsNote: 'Questa pagina è autorevole in inglese. Traduzioni complete in arrivo.',
    trashTitle: 'Il mio cestino',
    trashEmpty: 'Il cestino è vuoto.',
    trashProOnly: 'Passa a Pro per ripristinare i progetti eliminati.',
    restore: 'Ripristina',
    upgrade: 'Aggiorna',
  },
  hi: {
    // Common
    loading: 'लोड हो रहा है...',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    create: 'बनाएं',
    back: 'वापस',
    next: 'आगे',

    // Navigation
    home: 'होम',
    dashboard: 'डैशबोर्ड',
    gallery: 'गैलरी',
    pricing: 'मूल्य निर्धारण',
    settings: 'सेटिंग्स',
    logout: 'लॉग आउट',
    login: 'लॉगिन',
    signUp: 'साइन अप',

    // Landing
    heroTitle: 'AI के साथ गेम बनाएं',
    heroSubtitle: 'बिना कोड। बिना सीमा। सिर्फ शुद्ध रचनात्मकता।',
    startCreating: 'बनाना शुरू करें',
    exploreGames: 'गेम्स एक्सप्लोर करें',
    features: 'विशेषताएं',
    featureAI: 'AI कोपायलट',
    featureAIDesc: 'अपने गेम का वर्णन करें। हमारा AI इसे बनाता है।',
    featureVisual: 'विजुअल एडिटर',
    featureVisualDesc: 'बिना कोड के 3D दृश्य संपादित करें।',
    featureCommunity: 'समुदाय',
    featureCommunityDesc: 'प्रकाशित करें, रीमिक्स करें, साझा करें।',
    footerTagline: 'AI-संचालित गेम निर्माण मंच।',
    quickLinks: 'त्वरित लिंक',
    legal: 'कानूनी',
    copyright: '© 2026 PlayMold. सर्वाधिकार सुरक्षित।',
    privacy: 'गोपनीयता',
    terms: 'शर्तें',

    // Splash
    welcomeTo: 'आपका स्वागत है',
    selectLanguage: 'अपनी भाषा चुनें',
    continueBtn: 'जारी रखें',
    poweredBy: 'AI द्वारा संचालित',

    // Legal + trash
    privacyPolicyTitle: 'गोपनीयता नीति',
    termsTitle: 'सेवा की शर्तें',
    lastUpdated: 'अंतिम अद्यतन: जनवरी 2026',
    legalTranslationsNote: 'यह पृष्ठ अंग्रेजी में आधिकारिक है। पूर्ण अनुवाद जल्द ही आ रहे हैं।',
    trashTitle: 'मेरा कचरा',
    trashEmpty: 'कचरा खाली है।',
    trashProOnly: 'हटाई गई परियोजनाओं को पुनर्स्थापित करने के लिए Pro में अपग्रेड करें।',
    restore: 'पुनर्स्थापित करें',
    upgrade: 'अपग्रेड करें',
  },
};

const I18nContext = createContext();

// Detect browser locale and map to supported language (2-letter code).
// Special case: zh-* → zh, pt-BR → pt, ar-* → ar, etc.
function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return null;
  const raw = (navigator.language || (navigator.languages && navigator.languages[0]) || '').toLowerCase();
  if (!raw) return null;
  const primary = raw.split('-')[0];
  return LANGUAGES[primary] ? primary : null;
}

export function I18nProvider({ children }) {
  // Priority: (1) explicit user choice in localStorage, (2) auto-detected browser locale, (3) English
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('playmold_language');
    if (stored && LANGUAGES[stored]) return stored;
    return detectBrowserLanguage() || null;
  });

  // Show splash only if user has NOT explicitly chosen a language yet.
  // Auto-detected language does NOT skip splash — user still gets to pick — but it's pre-selected.
  const [showSplash, setShowSplash] = useState(() => {
    return !localStorage.getItem('playmold_language');
  });

  useEffect(() => {
    if (language) {
      // Persist ONLY when user explicitly picks (via selectLanguage) — see below.
      // Auto-detected language is applied for rendering but not stored so we still show splash on next visit.
      document.documentElement.dir = LANGUAGES[language]?.dir || 'ltr';
      document.documentElement.lang = language;
    }
  }, [language]);

  const t = (key) => {
    const lang = language || 'en';
    return translations[lang]?.[key] || translations['en']?.[key] || key;
  };

  const selectLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('playmold_language', lang);
    setShowSplash(false);
  };

  return (
    <I18nContext.Provider value={{ 
      language: language || 'en', 
      setLanguage: selectLanguage, 
      t, 
      showSplash,
      setShowSplash,
      LANGUAGES,
      detectedLanguage: detectBrowserLanguage(),
    }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
